import { Errors, isDefined, Option, panic, SyncStream, Terminable, Terminator, TimeSpan, UUID } from "@opendaw/lib-std";
import { AudioData } from "@opendaw/lib-dsp";
import { ApparatDeviceBox, SpielwerkDeviceBox, WerkstattDeviceBox } from "@opendaw/studio-boxes";
import { Communicator, Messenger, Promises, Wait } from "@opendaw/lib-runtime";
import { AnimationFrame } from "@opendaw/lib-dom";
import { EngineStateSchema, ExportConfiguration, ScriptCompiler } from "@opendaw/studio-adapters";
import { WclapBundles } from "./wclap";
import { MIDIReceiver } from "./midi";
// The engine worker every offline render runs in: the wasm engine, installed by `WasmEngine.install`.
// studio-core cannot import studio-core-wasm (that package depends on THIS one), so the url + artifact
// attachment are injected here rather than imported.
let engineWorker = Option.None;
export class OfflineEngineRenderer {
    // `attachment` travels to the worker as `config.variant` (the wasm artifacts base url).
    static install(url, attachment) {
        console.debug(`OfflineEngineWorkerUrl: '${url}'`);
        engineWorker = Option.wrap({ url, attachment });
    }
    static isInstalled() { return engineWorker.nonEmpty(); }
    static async create(source, optExportConfiguration, sampleRate = 48_000, abortSignal) {
        const numStems = ExportConfiguration.countStems(optExportConfiguration);
        if (numStems === 0) {
            return panic("Nothing to export");
        }
        if (isDefined(abortSignal) && abortSignal.aborted) {
            return Promise.reject(Errors.AbortError);
        }
        const numberOfChannels = numStems * 2;
        const engine = engineWorker.unwrap("No engine worker installed (WasmEngine.install must run first)");
        const worker = new Worker(engine.url, { type: "module" });
        const messenger = Messenger.for(worker);
        const protocol = Communicator.sender(messenger.channel("offline-engine"), dispatcher => new class {
            initialize(enginePort, config) {
                return dispatcher.dispatchAndReturn(this.initialize, enginePort, config);
            }
            addModule(code) {
                return dispatcher.dispatchAndReturn(this.addModule, code);
            }
            render(config) {
                return dispatcher.dispatchAndReturn(this.render, config);
            }
            step(samples) {
                return dispatcher.dispatchAndReturn(this.step, samples);
            }
            stop() { dispatcher.dispatchAndForget(this.stop); }
        });
        const channel = new MessageChannel();
        const engineStateIO = EngineStateSchema();
        const reader = SyncStream.reader(engineStateIO, () => { });
        const controlFlagsBuffer = new SharedArrayBuffer(4);
        const terminator = new Terminator();
        const engineMessenger = Messenger.for(channel.port2);
        Communicator.executor(engineMessenger.channel("engine-to-client"), {
            log: (message) => console.log("OFFLINE-ENGINE", message),
            error: (reason) => console.error("OFFLINE-ENGINE", reason),
            ready: () => { },
            fetchAudio: (uuid) => new Promise((resolve, reject) => {
                const handler = source.sampleManager.getOrCreate(uuid);
                const subscription = handler.subscribe(state => {
                    if (state.type === "error") {
                        reject(new Error(state.reason));
                        subscription.terminate();
                    }
                    else if (state.type === "loaded") {
                        resolve(handler.data.unwrap("handler.data"));
                        subscription.terminate();
                    }
                });
            }),
            fetchSoundfont: (uuid) => new Promise((resolve, reject) => {
                const handler = source.soundfontManager.getOrCreate(uuid);
                const subscription = handler.subscribe(state => {
                    if (state.type === "error") {
                        reject(new Error(state.reason));
                        subscription.terminate();
                    }
                    else if (state.type === "loaded") {
                        resolve(handler.soundfont.unwrap("handler.soundfont"));
                        subscription.terminate();
                    }
                });
            }),
            fetchNamWasm: async () => {
                const url = new URL("@opendaw/nam-wasm/nam.wasm", import.meta.url);
                const response = await fetch(url);
                return response.arrayBuffer();
            },
            fetchWclapBundle: (url) => WclapBundles.fetch(url),
            wclapSend: () => { },
            wclapState: () => { },
            wclapParams: () => { },
            wclapParam: () => { },
            wclapHovered: () => { },
            wclapResizeGui: () => { },
            wclapStatus: () => { },
            wclapRequestSave: () => { },
            notifyClipSequenceChanges: () => { },
            switchMarkerState: () => { },
            recordingStarted: () => { },
            deviceMessage: (uuid, message) => {
                console.warn(`OFFLINE-ENGINE device(${uuid}): ${message}`);
            }
        });
        const engineCommands = Communicator.sender(engineMessenger.channel("engine-commands"), dispatcher => new class {
            play() { dispatcher.dispatchAndForget(this.play); }
            stop(reset) { dispatcher.dispatchAndForget(this.stop, reset); }
            setPosition(position) { dispatcher.dispatchAndForget(this.setPosition, position); }
            prepareRecordingState(countIn, generation) { dispatcher.dispatchAndForget(this.prepareRecordingState, countIn, generation); }
            stopRecording() { dispatcher.dispatchAndForget(this.stopRecording); }
            queryLoadingComplete() { return dispatcher.dispatchAndReturn(this.queryLoadingComplete); }
            panic() { dispatcher.dispatchAndForget(this.panic); }
            noteSignal(signal) { dispatcher.dispatchAndForget(this.noteSignal, signal); }
            ignoreNoteRegion(uuid) { dispatcher.dispatchAndForget(this.ignoreNoteRegion, uuid); }
            suspendAutomation(uuid) { dispatcher.dispatchAndForget(this.suspendAutomation, uuid); }
            scheduleClipPlay(clipIds) { dispatcher.dispatchAndForget(this.scheduleClipPlay, clipIds); }
            scheduleClipStop(trackIds) { dispatcher.dispatchAndForget(this.scheduleClipStop, trackIds); }
            setupMIDI(port, buffer) { dispatcher.dispatchAndForget(this.setupMIDI, port, buffer); }
            updateMonitoringMap(map) { dispatcher.dispatchAndForget(this.updateMonitoringMap, map); }
            loadClickSound(index, data) { dispatcher.dispatchAndForget(this.loadClickSound, index, data); }
            setFrozenAudio(uuid, audioData) { dispatcher.dispatchAndForget(this.setFrozenAudio, uuid, audioData); }
            wclapOpenGui(uuid) { return dispatcher.dispatchAndReturn(this.wclapOpenGui, uuid); }
            wclapCloseGui(uuid) { dispatcher.dispatchAndForget(this.wclapCloseGui, uuid); }
            wclapResizeGui(uuid, width, height) {
                return dispatcher.dispatchAndReturn(this.wclapResizeGui, uuid, width, height);
            }
            wclapReceive(uuid, bytes) { dispatcher.dispatchAndForget(this.wclapReceive, uuid, bytes); }
            wclapSaveState(uuid) { dispatcher.dispatchAndForget(this.wclapSaveState, uuid); }
            wclapDescribe(url) { return dispatcher.dispatchAndReturn(this.wclapDescribe, url); }
            terminate() { dispatcher.dispatchAndForget(this.terminate); }
        });
        channel.port2.start();
        terminator.own(source.liveStreamReceiver.connect(engineMessenger.channel("engine-live-data")));
        const { port, sab } = terminator.own(MIDIReceiver.create(() => 0, (deviceId, data, relativeTimeInMs) => source.receivedMIDIFromEngine(deviceId, data, relativeTimeInMs)));
        // The SAME wrapper as the live worklet path (`ScriptCompiler.wrap`): the registry entry must carry
        // `params`/`samples` too — the WASM script bridge reads them (the TS worker ignores the extras).
        const loadScriptDevice = async (code, headerPattern, headerTag, registryName, functionName, uuid) => {
            const match = code.match(headerPattern);
            if (match === null) {
                return;
            }
            const userCode = code.slice(match[0].length);
            const update = parseInt(match[3]);
            await protocol.addModule(ScriptCompiler.wrap({ headerTag, registryName, functionName }, uuid, update, userCode));
        };
        const initialize = async () => {
            for (const box of source.boxGraph.boxes()) {
                if (box instanceof WerkstattDeviceBox) {
                    await loadScriptDevice(box.code.getValue(), /^\/\/ @werkstatt (\w+) (\d+) (\d+)\n/, "werkstatt", "werkstattProcessors", "werkstatt", UUID.toString(box.address.uuid));
                }
                else if (box instanceof SpielwerkDeviceBox) {
                    await loadScriptDevice(box.code.getValue(), /^\/\/ @spielwerk (\w+) (\d+) (\d+)\n/, "spielwerk", "spielwerkProcessors", "spielwerk", UUID.toString(box.address.uuid));
                }
                else if (box instanceof ApparatDeviceBox) {
                    await loadScriptDevice(box.code.getValue(), /^\/\/ @apparat (\w+) (\d+) (\d+)\n/, "apparat", "apparatProcessors", "apparat", UUID.toString(box.address.uuid));
                }
            }
            await protocol.initialize(channel.port1, {
                sampleRate,
                numberOfChannels,
                syncStreamBuffer: reader.buffer,
                controlFlagsBuffer,
                project: source.toArrayBuffer(),
                exportConfiguration: optExportConfiguration.unwrapOrUndefined(),
                variant: engine.attachment
            });
        };
        const { promise: abortPromise, reject: rejectOnAbort } = Promise.withResolvers();
        const onAbort = () => rejectOnAbort(Errors.AbortError);
        if (isDefined(abortSignal)) {
            abortSignal.addEventListener("abort", onAbort, { once: true });
        }
        const result = await Promises.tryCatch(Promise.race([initialize(), abortPromise]));
        if (isDefined(abortSignal)) {
            abortSignal.removeEventListener("abort", onAbort);
        }
        if (result.status === "rejected") {
            terminator.terminate();
            channel.port1.close();
            channel.port2.close();
            worker.terminate();
            return Promise.reject(result.error);
        }
        engineCommands.setupMIDI(port, sab);
        return new OfflineEngineRenderer(worker, protocol, engineCommands, terminator, reader, engineStateIO, sampleRate, numberOfChannels);
    }
    static async start(source, optExportConfiguration, progress, abortSignal, sampleRate = 48_000) {
        const { timelineBox: { loopArea: { enabled } }, boxGraph } = source;
        const wasEnabled = enabled.getValue();
        boxGraph.beginTransaction();
        enabled.setValue(false);
        boxGraph.endTransaction();
        const range = optExportConfiguration.flatMap(cfg => Option.wrap(cfg.range));
        const { startPosition, endPosition } = range.match({
            none: () => ({ startPosition: 0, endPosition: source.lastRegionAction() }),
            some: r => r === "full"
                ? { startPosition: 0, endPosition: source.lastRegionAction() }
                : { startPosition: r.start, endPosition: r.end }
        });
        const maxDurationSeconds = source.tempoMap.intervalToSeconds(startPosition, endPosition) + 30;
        const result = await Promises.tryCatch(this.create(source, optExportConfiguration, sampleRate, abortSignal).then(renderer => renderer.render({ maxDurationSeconds }, startPosition, endPosition, progress, abortSignal)));
        boxGraph.beginTransaction();
        enabled.setValue(wasEnabled);
        boxGraph.endTransaction();
        if (result.status === "rejected") {
            return Promise.reject(result.error);
        }
        return result.value;
    }
    #worker;
    #protocol;
    #engineCommands;
    #terminator;
    #reader;
    #engineStateIO;
    #sampleRate;
    #numberOfChannels;
    #totalFrames = 0;
    constructor(worker, protocol, engineCommands, terminator, reader, engineStateIO, sampleRate, numberOfChannels) {
        this.#worker = worker;
        this.#protocol = protocol;
        this.#engineCommands = engineCommands;
        this.#terminator = terminator;
        this.#reader = reader;
        this.#engineStateIO = engineStateIO;
        this.#sampleRate = sampleRate;
        this.#numberOfChannels = numberOfChannels;
    }
    get sampleRate() { return this.#sampleRate; }
    get numberOfChannels() { return this.#numberOfChannels; }
    get totalFrames() { return this.#totalFrames; }
    async play() {
        this.#engineCommands.play();
        await this.#engineCommands.queryLoadingComplete();
    }
    stop() {
        this.#engineCommands.stop(true);
        this.#protocol.stop();
    }
    setPosition(position) {
        this.#engineCommands.setPosition(position);
    }
    async waitForLoading() {
        while (!await this.#engineCommands.queryLoadingComplete()) {
            await Wait.timeSpan(TimeSpan.millis(100));
        }
    }
    terminate() {
        this.#terminator.terminate();
        this.#worker.terminate();
    }
    async step(samples) {
        const channels = await this.#protocol.step(samples);
        this.#totalFrames += samples;
        return channels;
    }
    async render(config, startPosition, endPosition, progress, abortSignal) {
        if (isDefined(abortSignal) && abortSignal.aborted) {
            this.terminate();
            return Promise.reject(Errors.AbortError);
        }
        const { promise, reject, resolve } = Promise.withResolvers();
        let cancelled = false;
        const span = endPosition - startPosition;
        const polling = span > 0
            ? AnimationFrame.add(() => {
                this.#reader.tryRead();
                progress.setValue(Math.min(1.0, Math.max(0, this.#engineStateIO.object.position - startPosition) / span));
            })
            : Terminable.Empty;
        const onAbort = () => {
            polling.terminate();
            this.stop();
            this.terminate();
            cancelled = true;
            reject(Errors.AbortError);
        };
        if (isDefined(abortSignal)) {
            abortSignal.addEventListener("abort", onAbort, { once: true });
        }
        while (!await this.#engineCommands.queryLoadingComplete()) {
            await Wait.timeSpan(TimeSpan.millis(100));
        }
        if (startPosition !== 0) {
            this.setPosition(startPosition);
        }
        await this.play();
        this.#protocol.render(config).then(channels => {
            polling.terminate();
            if (isDefined(abortSignal)) {
                abortSignal.removeEventListener("abort", onAbort);
            }
            if (cancelled) {
                return;
            }
            progress.setValue(1.0);
            this.terminate();
            const numberOfFrames = channels[0].length;
            const audioData = AudioData.create(this.#sampleRate, numberOfFrames, this.#numberOfChannels);
            for (let channelIndex = 0; channelIndex < this.#numberOfChannels; channelIndex++) {
                audioData.frames[channelIndex].set(channels[channelIndex]);
            }
            resolve(audioData);
        }).catch(reason => {
            polling.terminate();
            if (isDefined(abortSignal)) {
                abortSignal.removeEventListener("abort", onAbort);
            }
            if (!cancelled) {
                this.terminate();
                reject(reason);
            }
        });
        return promise;
    }
}
